/**
 * 用户相关 Store
 * 负责用户信息管理、密码修改、用户设置等功能
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useCacheStore } from '../cache'
import { useAuthStore } from './authStore'
import { cacheKeys } from '../config'
import { userApi } from '@/https/api/user'
import type { UserUpdateRequest, ChangePasswordRequest } from '@/https/types/user'

export const useUserStore = defineStore('user', () => {
    const cacheStore = useCacheStore()
    const authStore = useAuthStore()

    // ==================== 状态管理 ====================

    // 用户操作加载状态
    const userOperationLoading = ref(false)

    // 用户操作错误信息
    const userOperationError = ref<string | null>(null)

    // 密码修改加载状态
    const changePasswordLoading = ref(false)

    // 用户设置（预留）
    const userSettings = ref<Record<string, unknown>>({})

    // ==================== 计算属性 ====================

    // 当前用户信息（从 auth store 获取）
    const currentUser = computed(() => authStore.currentUser)

    // 用户基本信息
    const userProfile = computed(() => {
        if (!currentUser.value) return null

        return {
            id: currentUser.value.id,
            nickname: currentUser.value.nickname,
            avatar: currentUser.value.avatar,
            avatar_path: (currentUser.value as unknown as { avatar_path?: string }).avatar_path,
            gallery_paths: (currentUser.value as unknown as { gallery_paths?: string[] }).gallery_paths,
            phone: currentUser.value.phone,
            qq: currentUser.value.qq,
            wechat: currentUser.value.wechat,
            roles: currentUser.value.roles,
            can_access_admin: currentUser.value.can_access_admin,
            created_at: currentUser.value.created_at,
            last_login: currentUser.value.last_login,
            is_active: currentUser.value.is_active,
            has_password: currentUser.value.has_password
        }
    })

    // 用户联系方式
    const userContacts = computed(() => {
        if (!currentUser.value) return null

        return {
            phone: currentUser.value.phone,
            qq: currentUser.value.qq,
            wechat: currentUser.value.wechat
        }
    })

    // 是否已完善基本信息
    const isProfileComplete = computed(() => {
        if (!currentUser.value) return false

        return !!(
            currentUser.value.nickname &&
            (currentUser.value.qq || currentUser.value.wechat)
        )
    })

    // 用户状态信息
    const userStatus = computed(() => {
        if (!currentUser.value) return null

        return {
            isActive: currentUser.value.is_active,
            canAccessAdmin: currentUser.value.can_access_admin,
            roles: currentUser.value.roles,
            hasRoles: currentUser.value.roles && currentUser.value.roles.name
        }
    })

    // ==================== 用户信息管理 ====================

    /**
     * 更新用户信息 - 统一的更新方法
     * @param userData 用户数据
     */
    const updateUserInfo = async (userData: UserUpdateRequest): Promise<boolean> => {
        try {
            userOperationLoading.value = true
            userOperationError.value = null

            // 直接调用 authStore 的更新方法，避免重复逻辑
            const success = await authStore.updateUserInfo(userData)

            return success
        } catch (error) {
            console.error('更新用户信息失败:', error)
            userOperationError.value = error instanceof Error ? error.message : '更新用户信息失败'
            return false
        } finally {
            userOperationLoading.value = false
        }
    }

    // ==================== 密码管理 ====================

    /**
     * 修改密码
     * @param passwordData 密码数据
     */
    const changeUserPassword = async (passwordData: ChangePasswordRequest): Promise<boolean> => {
        try {
            changePasswordLoading.value = true
            userOperationError.value = null

            await cacheStore.smartCache(
                'user:change-password',
                () => userApi.changePassword(passwordData)
            )

            return true
        } catch (error) {
            console.error('修改密码失败:', error)
            userOperationError.value = error instanceof Error ? error.message : '修改密码失败'
            return false
        } finally {
            changePasswordLoading.value = false
        }
    }

    // ==================== 用户设置管理 ====================

    /**
     * 更新用户设置
     * @param settings 设置数据
     */
    const updateUserSettings = (settings: Record<string, unknown>): void => {
        Object.assign(userSettings.value, settings)

        // 缓存用户设置
        cacheStore.setCache(
            cacheKeys.user.settings,
            userSettings.value,
            cacheStore.getCacheTime('VERY_LONG')
        )
    }

    /**
     * 加载用户设置
     */
    const loadUserSettings = (): Record<string, unknown> => {
        const cached = cacheStore.getCache<Record<string, unknown>>(cacheKeys.user.settings)
        if (cached) {
            userSettings.value = cached
        }
        return userSettings.value
    }

    // ==================== 用户数据刷新 ====================

    /**
     * 刷新用户信息
     */
    const refreshUserProfile = async (): Promise<boolean> => {
        try {
            userOperationLoading.value = true
            userOperationError.value = null

            // 强制刷新用户信息
            const success = await authStore.loadUserProfile(true)
            return !!success
        } catch (error) {
            console.error('刷新用户信息失败:', error)
            userOperationError.value = error instanceof Error ? error.message : '刷新用户信息失败'
            return false
        } finally {
            userOperationLoading.value = false
        }
    }

    // ==================== 工具方法 ====================

    /**
     * 清除用户操作错误
     */
    const clearUserError = (): void => {
        userOperationError.value = null
    }

    // ==================== 初始化 ====================

    /**
     * 初始化用户 Store
     */
    const initUser = (): void => {
        // 加载用户设置
        if (authStore.isAuthenticated) {
            loadUserSettings()
        }
    }

    // ==================== 返回 API ====================

    return {
        // 状态
        userOperationLoading: computed(() => userOperationLoading.value),
        userOperationError: computed(() => userOperationError.value),
        changePasswordLoading: computed(() => changePasswordLoading.value),
        userSettings: computed(() => userSettings.value),

        // 计算属性
        currentUser,
        userProfile,
        userContacts,
        isProfileComplete,
        userStatus,

        // 用户信息管理
        updateUserInfo,
        refreshUserProfile,

        // 密码管理
        changeUserPassword,

        // 用户设置管理
        updateUserSettings,
        loadUserSettings,

        // 工具方法
        clearUserError,

        // 初始化
        initUser
    }
}) 